import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IimAutocompleteModule,
    IimMultiselectComponent,
    IimMultiselectPreferences,
    IimSpinnerModule,
    IimTableModule,
    TableSortDirection,
    TableSortState,
} from '@iim-angular/components';
import { NotificationMessageLevel, NotificationService, SortDirection, StorageService } from '@lib/core';
import { BC } from '@lib/shared/breadcrumbs/constants';
import { BreadcrumbService } from '@lib/shared/breadcrumbs/service';
import { DialogOptions } from '@lib/util';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, firstValueFrom } from 'rxjs';
import { PurchasingGroupAssignableUser } from '../models/purchasing-group-assignable-user.model';
import {
    PurchasingGroupCreateComponent,
    PurchasingGroupCreateDialogResult,
} from '../create/purchasing-group-create.component';
import { PurchasingGroupDataService } from '../services/purchasing-group-data.service';
import {
    PurchasingGroupSettingsComponent,
    PurchasingGroupSettingsDialogResult,
} from '../settings/purchasing-group-settings.component';
import { PurchasingGroupListStore } from './purchasing-group-list.store';
import { PurchasingGroupFilterOption } from './types/purchasing-group-filter-option';
import {
    IPurchasingGroupListFilterDto,
    PurchasingGroupListFilter,
    PurchasingGroupStatusFilter,
} from './types/purchasing-group-list-filter';
import {
    PurchasingGroupListSorting,
    PurchasingGroupSortBy,
} from './types/purchasing-group-list-sorting';

interface IPurchasingGroupRow {
    readonly id: string;
    readonly code: string;
    readonly name: string;
    readonly active: boolean;
    readonly companyCodes: ReadonlyArray<string>;
    readonly materialGroupCodes: ReadonlyArray<string>;
    readonly memberDisplays: ReadonlyArray<string>;
}

@Component({
    selector: 'lib-purchasing-group-list',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        BaseSiteLayoutModule,
        TranslateModule,
        IimTableModule,
        IimSpinnerModule,
        IimAutocompleteModule,
        IimMultiselectComponent,
        MatButtonModule,
    ],
    templateUrl: './purchasing-group-list.component.html',
    styleUrls: ['./purchasing-group-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PurchasingGroupListStore],
})
export class PurchasingGroupListComponent implements OnInit {
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly storage = inject(StorageService);

    public readonly store = inject(PurchasingGroupListStore);
    public readonly heading = marker('Einkäufergruppen');
    public readonly icon = 'fas fa-users';
    public readonly statusFilterChoices = PurchasingGroupStatusFilter;
    public readonly multiselectPreferences: IimMultiselectPreferences = {
        showSelectAll: true,
        removeLastValueOnBackspace: true,
    };

    public readonly codeControl = new FormControl('', { nonNullable: true });
    public readonly nameControl = new FormControl('', { nonNullable: true });
    public readonly companyCodesControl = new FormControl<PurchasingGroupFilterOption[]>([], {
        nonNullable: true,
    });
    public readonly materialGroupCodesControl = new FormControl<PurchasingGroupFilterOption[]>([], {
        nonNullable: true,
    });
    public readonly memberIdsControl = new FormControl<PurchasingGroupAssignableUser[]>([], {
        nonNullable: true,
    });

    private readonly filterInstance = signal<PurchasingGroupListFilter | null>(null);

    public readonly filterDto = computed<IPurchasingGroupListFilterDto>(() => {
        const filter = this.filterInstance();
        return filter
            ? filter.dto()
            : {
                  code: '',
                  name: '',
                  companyCodes: [],
                  materialGroupCodes: [],
                  memberIds: [],
                  status: PurchasingGroupStatusFilter.ALL,
              };
    });

    public readonly activeFilterCount = computed(() => {
        const filter = this.filterInstance();
        if (!filter) return 0;
        filter.dto();
        return filter.activeCount;
    });

    public readonly memberDisplayById = computed<ReadonlyMap<string, string>>(() => {
        const map = new Map<string, string>();
        for (const member of this.store.memberOptions()) {
            map.set(member.id, member.valueDisplay);
        }
        return map;
    });

    public readonly companyCodesContent = computed<string>(() =>
        this.formatCodeList(this.filterDto().companyCodes),
    );

    public readonly materialGroupCodesContent = computed<string>(() =>
        this.formatCodeList(this.filterDto().materialGroupCodes),
    );

    public readonly memberIdsContent = computed<string>(() => {
        const lookup = this.memberDisplayById();
        return this.formatCodeList(this.filterDto().memberIds.map((id) => lookup.get(id) ?? id));
    });

    public readonly statusContent = computed<string>(() => {
        const status = this.filterDto().status;
        if (status === PurchasingGroupStatusFilter.ACTIVE) return marker('Aktiv');
        if (status === PurchasingGroupStatusFilter.INACTIVE) return marker('Inaktiv');
        return '';
    });

    public readonly filteredRows = computed<ReadonlyArray<IPurchasingGroupRow>>(() => {
        const filter = this.filterInstance();
        const all = this.store.purchasingGroups();
        const matching = filter ? all.filter((pg) => filter.matches(pg)) : [...all];
        const lookup = this.memberDisplayById();
        return matching.map((pg) => ({
            id: pg.id,
            code: pg.code,
            name: pg.name,
            active: pg.active,
            companyCodes: pg.companyCodes,
            materialGroupCodes: pg.materialGroupCodes,
            memberDisplays: pg.memberIds.map((id) => lookup.get(id) ?? id),
        }));
    });

    constructor() {
        this.breadcrumbService.set(BC.SYSTEM.LIST, BC.SYSTEM.PURCHASING_GROUPS.LIST);
        this.subscribeTextFilters();
        this.subscribeMultiselectFilters();
    }

    public async ngOnInit(): Promise<void> {
        const params = this.route.snapshot.queryParams;
        const sorting = await this.store.initSorting(params);
        const filter = await PurchasingGroupListFilter.restore(this.storage, params);
        this.filterInstance.set(filter);
        const initial = filter.dto();
        this.codeControl.setValue(initial.code, { emitEvent: false });
        this.nameControl.setValue(initial.name, { emitEvent: false });
        this.syncRoute(sorting);
        this.loadAll();
        this.store.loadFilterOptions().subscribe(() => {
            this.populateMultiselectsFromFilter();
        });
    }

    private populateMultiselectsFromFilter(): void {
        const filter = this.filterInstance();
        if (!filter) return;
        const dto = filter.dto();
        this.companyCodesControl.setValue(
            this.store.companyCodeOptions().filter((option) => dto.companyCodes.includes(option.code)),
            { emitEvent: false },
        );
        this.materialGroupCodesControl.setValue(
            this.store
                .materialGroupOptions()
                .filter((option) => dto.materialGroupCodes.includes(option.code)),
            { emitEvent: false },
        );
        this.memberIdsControl.setValue(
            this.store.memberOptions().filter((member) => dto.memberIds.includes(member.id)),
            { emitEvent: false },
        );
    }

    public async openCreate(): Promise<void> {
        const dialogRef = this.dialog.open<
            PurchasingGroupCreateComponent,
            undefined,
            PurchasingGroupCreateDialogResult
        >(PurchasingGroupCreateComponent, DialogOptions.MEDIUM());
        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result?.created) {
            this.loadAll();
        }
    }

    public openDetails(id: string): void {
        void this.router.navigate([id], { relativeTo: this.route });
    }

    public openSettings(): void {
        this.dialog.open<
            PurchasingGroupSettingsComponent,
            undefined,
            PurchasingGroupSettingsDialogResult
        >(PurchasingGroupSettingsComponent, DialogOptions.SMALL());
    }

    public onSortChange(sortState: TableSortState): void {
        const sortDirection =
            sortState?.direction === TableSortDirection.DESC ? SortDirection.DESC : SortDirection.ASC;
        const sortBy =
            sortState?.column.sortingHeaderName === PurchasingGroupSortBy.NAME
                ? PurchasingGroupSortBy.NAME
                : PurchasingGroupSortBy.CODE;
        const sorting = new PurchasingGroupListSorting(sortBy, sortDirection);
        this.store.setSorting(sortBy, sortDirection);
        this.syncRoute(sorting);
    }

    public async onQuickStatusChange(event: Event): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        const select = event.target as HTMLSelectElement;
        await filter.setStatus(this.toStatus(select.value));
        this.syncRoute(this.currentSorting());
    }

    public async clearCode(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        this.codeControl.setValue('', { emitEvent: false });
        await filter.clearKey('code');
        this.syncRoute(this.currentSorting());
    }

    public async clearName(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        this.nameControl.setValue('', { emitEvent: false });
        await filter.clearKey('name');
        this.syncRoute(this.currentSorting());
    }

    public async clearStatus(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        await filter.clearKey('status');
        this.syncRoute(this.currentSorting());
    }

    public async clearCompanyCodes(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        this.companyCodesControl.setValue([], { emitEvent: false });
        await filter.clearKey('companyCodes');
        this.syncRoute(this.currentSorting());
    }

    public async clearMaterialGroupCodes(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        this.materialGroupCodesControl.setValue([], { emitEvent: false });
        await filter.clearKey('materialGroupCodes');
        this.syncRoute(this.currentSorting());
    }

    public async clearMembers(): Promise<void> {
        const filter = this.filterInstance();
        if (!filter) return;
        this.memberIdsControl.setValue([], { emitEvent: false });
        await filter.clearKey('memberIds');
        this.syncRoute(this.currentSorting());
    }

    private subscribeTextFilters(): void {
        this.codeControl.valueChanges
            .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
            .subscribe(async (value) => {
                const filter = this.filterInstance();
                if (!filter) return;
                await filter.update({ code: value });
                this.syncRoute(this.currentSorting());
            });

        this.nameControl.valueChanges
            .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
            .subscribe(async (value) => {
                const filter = this.filterInstance();
                if (!filter) return;
                await filter.update({ name: value });
                this.syncRoute(this.currentSorting());
            });
    }

    private subscribeMultiselectFilters(): void {
        this.companyCodesControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (options) => {
                const filter = this.filterInstance();
                if (!filter) return;
                const newCodes = options.map((o) => o.code);
                if (this.stringArrayEquals(filter.dto().companyCodes, newCodes)) return;
                await filter.update({ companyCodes: newCodes });
                this.syncRoute(this.currentSorting());
            });

        this.materialGroupCodesControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (options) => {
                const filter = this.filterInstance();
                if (!filter) return;
                const newCodes = options.map((o) => o.code);
                if (this.stringArrayEquals(filter.dto().materialGroupCodes, newCodes)) return;
                await filter.update({ materialGroupCodes: newCodes });
                this.syncRoute(this.currentSorting());
            });

        this.memberIdsControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(async (members) => {
                const filter = this.filterInstance();
                if (!filter) return;
                const newIds = members.map((m) => m.id);
                if (this.stringArrayEquals(filter.dto().memberIds, newIds)) return;
                await filter.update({ memberIds: newIds });
                this.syncRoute(this.currentSorting());
            });
    }

    private stringArrayEquals(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    private currentSorting(): PurchasingGroupListSorting {
        return new PurchasingGroupListSorting(this.store.sortBy(), this.store.sortDirection());
    }

    private syncRoute(sorting: PurchasingGroupListSorting): void {
        const filter = this.filterInstance();
        const params: Params = {
            ...sorting.toRouteParams(),
            ...(filter?.clearedRouteParams ?? {}),
            ...(filter?.asRouteParams ?? {}),
        };
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: params,
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private loadAll(): void {
        this.store.loadAll().subscribe({
            error: (error) => {
                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Fehler beim Laden der Daten'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            },
        });
    }

    private toStatus(value: string): PurchasingGroupStatusFilter {
        if (
            value === PurchasingGroupStatusFilter.ACTIVE ||
            value === PurchasingGroupStatusFilter.INACTIVE
        ) {
            return value;
        }
        return PurchasingGroupStatusFilter.ALL;
    }

    private formatCodeList(codes: ReadonlyArray<string>): string {
        if (codes.length === 0) return '';
        if (codes.length <= 3) return codes.join(', ');
        return `${codes.slice(0, 3).join(', ')} +${codes.length - 3}`;
    }
}
